import {
  filterObjectProperties, mapObjectProperties,
} from '~/_shared/utils/collections/collections';
import {
  boundaryTerritoryGroupMatchingChanged,
  defaultBoundaryFillActivated,
  extraBoundaryFillActivated,
  extraBoundaryFillAdded, extraBoundaryFillRemoved, extraBoundaryFillsLoaded, extraBoundaryFillUpdated,
} from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/boundaryExtraFills.actionCreators';
import type { BoundaryExtraFillAction } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/boundaryExtraFills.actions';
import type { BoundaryFillConfig } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/extraFills.types';
import { type BoundaryTerritoryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import type { BoundaryTerritoryDetailsAction } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.action';
import { BOUNDARY_TERRITORY_DETAILS_FETCH_DETAILS_SUCCESS } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.actionTypes';
import type { BTGWithActiveExtraFill } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import type { BoundaryTerritoryGroupsAction } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.action';
import type { MapAction } from '~/store/map/map.action';
import { MAP_RESET } from '~/store/map/map.actionTypes';
import type { MapSettingsToolsStateBoundaryStateAction } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.action';
import { MAP_SETTINGS_TOOLS_STATE_BOUNDARY_SET_FILTER } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.actionTypes';
import type { BoundaryFilter } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.state';
import type { SpreadsheetDataAction } from '~/store/spreadsheetData/spreadsheetData.action';
import { SPREADSHEET_RESET_STATE_AND_REFETCH_DATA } from '~/store/spreadsheetData/spreadsheetData.actionTypes';

type BoundaryTerritoryGroupData = {
  readonly activeBoundaryFillId: Uuid | null; // Nullsy means no fill or the base fill are active.
  readonly extraFills: ReadonlyArray<BoundaryFillConfig>;
};

type ExtraFillInfo = BTGWithActiveExtraFill['activeFillInfo'];

type BoundaryTerritoryToolState = {
  readonly extraFillDataPerBTGId: {
    readonly [boundaryTerritoryGroupId: BoundaryTerritoryGroupId]: BoundaryTerritoryGroupData;
  };
  readonly defaultFillFilterHashPerBTGId: {
    readonly [boundaryTerritoryGroupId: BoundaryTerritoryGroupId]: string | undefined;
  };
  readonly extraFillInfoPerFillId: {
    readonly [fillId: Uuid]: ExtraFillInfo;
  };
  readonly fillFilters: {
    readonly [fillId: Uuid]: BoundaryFilter | undefined;
  };
};

const initialState: BoundaryTerritoryToolState = {
  extraFillDataPerBTGId: {},
  defaultFillFilterHashPerBTGId: {},
  extraFillInfoPerFillId: {},
  fillFilters: {},
};

// Subreducer to handle actions for a specific boundaryTerritoryGroupId
const boundaryTerritoryGroupReducer = (
  groupData: BoundaryTerritoryGroupData = {
    activeBoundaryFillId: null,
    extraFills: [],
  },
  action: BoundaryExtraFillAction | MapAction,
): BoundaryTerritoryGroupData => {
  if (extraBoundaryFillAdded.match(action)) {
    const { boundaryFillConfig } = action.payload;
    return {
      ...groupData,
      extraFills: [...groupData.extraFills, boundaryFillConfig],
      activeBoundaryFillId: boundaryFillConfig.boundaryFillId ?? null,
    };
  }

  if (extraBoundaryFillUpdated.match(action)) {
    const { boundaryFillConfig } = action.payload;
    return {
      ...groupData,
      extraFills: groupData.extraFills.map((stateFill) => {
        if (stateFill.boundaryFillId === boundaryFillConfig.boundaryFillId) {
          return boundaryFillConfig;
        }
        else {
          return stateFill;
        }
      }),
      activeBoundaryFillId: boundaryFillConfig.boundaryFillId ?? null,
    };
  }

  if (extraBoundaryFillRemoved.match(action)) {
    const { boundaryFillId } = action.payload;
    return {
      ...groupData,
      activeBoundaryFillId: groupData.activeBoundaryFillId === boundaryFillId ? null : groupData.activeBoundaryFillId,
      extraFills: groupData.extraFills.filter((fill) => fill.boundaryFillId !== boundaryFillId),
    };
  }

  if (extraBoundaryFillActivated.match(action)) {
    const { extraBoundaryFillId } = action.payload;
    return {
      ...groupData,
      activeBoundaryFillId: extraBoundaryFillId,
    };
  }

  if (defaultBoundaryFillActivated.match(action)) {
    return {
      ...groupData,
      activeBoundaryFillId: null,
    };
  }

  if (action.type === MAP_RESET) {
    return {
      ...groupData,
      activeBoundaryFillId: null,
    };
  }

  return groupData;
};

export const boundaryTerritoryToolReducer = (
  state = initialState,
  action: BoundaryExtraFillAction |
    MapAction |
    SpreadsheetDataAction |
    BoundaryTerritoryDetailsAction |
    BoundaryTerritoryGroupsAction |
    MapSettingsToolsStateBoundaryStateAction,
): BoundaryTerritoryToolState => {
  if (extraBoundaryFillActivated.match(action) || defaultBoundaryFillActivated.match(action)
  ) {
    const { boundaryTerritoryGroupId } = action.payload;
    const currentGroupData = state.extraFillDataPerBTGId[boundaryTerritoryGroupId] || {
      activeBoundaryFillId: null,
      extraFills: [],
    };

    const updatedGroupData = boundaryTerritoryGroupReducer(currentGroupData, action);

    return {
      ...state,
      extraFillDataPerBTGId: {
        ...state.extraFillDataPerBTGId,
        [boundaryTerritoryGroupId]: updatedGroupData,
      },
    };
  }

  if (extraBoundaryFillRemoved.match(action)) {
    const { boundaryTerritoryGroupId, boundaryFillId } = action.payload;
    const currentGroupData = state.extraFillDataPerBTGId[boundaryTerritoryGroupId] || {
      activeBoundaryFillId: null,
      extraFills: [],
    };

    const updatedGroupData = boundaryTerritoryGroupReducer(currentGroupData, action);

    const fillFiltersWithoutRemovedFill = filterObjectProperties(
      (_, fillId) => fillId !== boundaryFillId,
      state.fillFilters,
    );

    return {
      ...state,
      extraFillDataPerBTGId: {
        ...state.extraFillDataPerBTGId,
        [boundaryTerritoryGroupId]: updatedGroupData,
      },
      extraFillInfoPerFillId: filterObjectProperties(
        (_, fillId) => fillId !== boundaryFillId,
        state.extraFillInfoPerFillId,
      ),
      fillFilters: fillFiltersWithoutRemovedFill,
    };
  }

  if (extraBoundaryFillAdded.match(action) || extraBoundaryFillUpdated.match(action)) {
    const { boundaryTerritoryGroupId } = action.payload;
    const currentGroupData = state.extraFillDataPerBTGId[boundaryTerritoryGroupId] || {
      activeBoundaryFillId: null,
      extraFills: [],
    };

    const updatedGroupData = boundaryTerritoryGroupReducer(currentGroupData, action);

    return {
      ...state,
      extraFillDataPerBTGId: {
        ...state.extraFillDataPerBTGId,
        [boundaryTerritoryGroupId]: updatedGroupData,
      },
      extraFillInfoPerFillId: filterObjectProperties(
        (_, fillId) => fillId !== action.payload.boundaryFillConfig.boundaryFillId,
        state.extraFillInfoPerFillId,
      ),
    };
  }

  if (extraBoundaryFillsLoaded.match(action)) {
    return {
      ...state,
      extraFillDataPerBTGId: mapObjectProperties((extraFills) => {
        return {
          extraFills,
          activeBoundaryFillId: null,
        };
      }, action.payload.extraFills),
    };
  }

  if (action.type === MAP_RESET) {
    return {
      ...state,
      extraFillDataPerBTGId: mapObjectProperties(groupData =>
        boundaryTerritoryGroupReducer(groupData, action),
      state.extraFillDataPerBTGId),
      extraFillInfoPerFillId: {},
      fillFilters: {},
    };
  }

  if (action.type === BOUNDARY_TERRITORY_DETAILS_FETCH_DETAILS_SUCCESS) {
    const { results, filterHash } = action.payload;

    const newExtraFillInfos: typeof state.extraFillInfoPerFillId = results
      .reduce((extraFillInfos, actionResult) => {
        if (actionResult.activeFillId) {
          extraFillInfos[actionResult.activeFillId] = {
            btgDetails: actionResult.details,
            filterHash,
          };
        }
        return extraFillInfos;
      }, { ...state.extraFillInfoPerFillId });

    const newDefaultFillHashes: typeof state.defaultFillFilterHashPerBTGId = results
      .reduce((defaultFillInfos, actionResult) => {
        if (!actionResult.activeFillId && actionResult.details.boundaryTerritoryGroupId) {
          defaultFillInfos[actionResult.details.boundaryTerritoryGroupId] = filterHash;
        }

        return defaultFillInfos;
      }, { ...state.defaultFillFilterHashPerBTGId });

    return {
      ...state,
      extraFillInfoPerFillId: newExtraFillInfos,
      defaultFillFilterHashPerBTGId: newDefaultFillHashes,
    };
  }

  if (action.type === SPREADSHEET_RESET_STATE_AND_REFETCH_DATA) {
    return {
      ...state,
      extraFillInfoPerFillId: {},
      defaultFillFilterHashPerBTGId: {},
      fillFilters: {},
    };
  }

  if (action.type === MAP_SETTINGS_TOOLS_STATE_BOUNDARY_SET_FILTER && action.payload.activeFillId) {
    return {
      ...state,
      fillFilters: {
        ...state.fillFilters,
        [action.payload.activeFillId]: action.payload.filter,
      },
    };
  }

  if (boundaryTerritoryGroupMatchingChanged.match(action)) {
    const { boundaryTerritoryGroupId } = action.payload;

    const updatedExtraFillInfoPerFillId = filterObjectProperties(
      (fillInfo) => fillInfo?.btgDetails?.boundaryTerritoryGroupId !== boundaryTerritoryGroupId,
      state.extraFillInfoPerFillId,
    );

    const updatedDefaultFillFilterHashPerBTGId = filterObjectProperties(
      (_filterHash, btgId) => Number(btgId) !== boundaryTerritoryGroupId,
      state.defaultFillFilterHashPerBTGId);

    return {
      ...state,
      extraFillInfoPerFillId: updatedExtraFillInfoPerFillId,
      defaultFillFilterHashPerBTGId: updatedDefaultFillFilterHashPerBTGId,
    };
  }

  return state;
};
